import { settingsEntityConfig } from '@/generated/entity-config'
import * as messages from '@/integrations/paraglide/messages/_index.js'
import type { FormSection } from '@/shared/forms/GenericForm'

type SettingsSectionValues = Record<string, unknown>

export type SettingsSectionKey =
	| 'contactSettings'
	| 'loginSettings'
	| 'phoneSettings'
	| 'generalSettings'
	| 'setClipSettings'
	| 'recordingSettings'

export type SettingsFormValues = Record<
	SettingsSectionKey,
	SettingsSectionValues
>

export type TenantSettingsSectionKey =
	| 'generalSettings'
	| 'contactSettings'
	| 'loginSettings'
	| 'phoneSettings'
	| 'setClipSettings'
	| 'recordingSettings'

export type TenantSettingsDto = {
	contactSettings: SettingsSectionValues
	loginSettings: SettingsSectionValues
	phoneSettings: SettingsSectionValues
	generalSettings: SettingsSectionValues
	setClipSettings: SettingsSectionValues
	recordingSettings: SettingsSectionValues
}

export type TemplateSettingsDto = Partial<SettingsFormValues>
export type TenantSettingsValues = Partial<SettingsFormValues>

type SettingsTab<TKey extends string> = {
	key: TKey
	label: string
	slug: string
}

const generatedSettingsSections = settingsEntityConfig.settings.sections
type GeneratedSettingsSectionKey = keyof typeof generatedSettingsSections

function hasGeneratedSettingsSectionKey(
	value: string,
): value is GeneratedSettingsSectionKey {
	return value in generatedSettingsSections
}

function resolveGeneratedSettingsSectionKey(
	...candidates: string[]
): GeneratedSettingsSectionKey {
	const key = candidates.find(hasGeneratedSettingsSectionKey)
	if (!key) {
		throw new Error(
			`Missing generated settings section for ${candidates.join(', ')}`,
		)
	}
	return key
}

const generatedSettingsSectionKeyByAlias = {
	general: resolveGeneratedSettingsSectionKey('general'),
	contact: resolveGeneratedSettingsSectionKey('contact'),
	login: resolveGeneratedSettingsSectionKey('login'),
	phone: resolveGeneratedSettingsSectionKey('phone'),
	setClip: resolveGeneratedSettingsSectionKey('setClip', 'setclip'),
	recording: resolveGeneratedSettingsSectionKey('recording'),
} as const

type SettingsSectionAlias = keyof typeof generatedSettingsSectionKeyByAlias

type SettingsSectionMeta = {
	label: string
	EntitySection: SettingsSectionAlias
	slug?: string
}

function getSettingsSectionSlug(meta: SettingsSectionMeta) {
	return meta.slug ?? getGeneratedSettingsSection(meta.EntitySection).key
}

function getGeneratedSettingsSection(EntitySection: SettingsSectionAlias) {
	return generatedSettingsSections[
		generatedSettingsSectionKeyByAlias[EntitySection]
	]
}

function getGeneratedSettingsSectionPath(EntitySection: SettingsSectionAlias) {
	const path = getGeneratedSettingsSection(EntitySection).endpoints.load?.path
	return typeof path === 'string' ? path : undefined
}

const settingsSectionMeta = {
	generalSettings: {
		label: messages.settings_tab_general(),
		EntitySection: 'general',
	},
	contactSettings: {
		label: messages.settings_tab_contact(),
		EntitySection: 'contact',
	},
	loginSettings: {
		label: messages.settings_tab_login(),
		EntitySection: 'login',
	},
	phoneSettings: {
		label: messages.settings_tab_softphone(),
		EntitySection: 'phone',
	},
	setClipSettings: {
		label: messages.settings_tab_setClip(),
		slug: 'setclip',
		EntitySection: 'setClip',
	},
	recordingSettings: {
		label: messages.settings_tab_recording(),
		EntitySection: 'recording',
	},
} as const satisfies Record<SettingsSectionKey, SettingsSectionMeta>

const tenantSettingsSectionMeta = {
	...settingsSectionMeta,
} satisfies Record<TenantSettingsSectionKey, SettingsSectionMeta>

export const settingsSectionLabelByKey: Record<SettingsSectionKey, string> =
	Object.fromEntries(
		Object.entries(settingsSectionMeta).map(([key, value]) => [
			key,
			value.label,
		]),
	) as unknown as Record<SettingsSectionKey, string>

export const settingsSectionKeyToSlug: Record<SettingsSectionKey, string> =
	Object.fromEntries(
		Object.entries(settingsSectionMeta).map(([key, value]) => [
			key,
			getSettingsSectionSlug(value),
		]),
	) as Record<SettingsSectionKey, string>

export const settingsSectionSlugToKey = Object.fromEntries(
	Object.entries(settingsSectionKeyToSlug).map(([key, value]) => [value, key]),
) as Record<string, SettingsSectionKey>

export const settingsTabs = Object.entries(settingsSectionMeta).map(
	([key, value]) => ({
		key: key as SettingsSectionKey,
		label: value.label,
		slug: getSettingsSectionSlug(value),
	}),
) satisfies Array<SettingsTab<SettingsSectionKey>>

const settingsSectionKeys = new Set<SettingsSectionKey>(
	Object.keys(settingsSectionMeta) as SettingsSectionKey[],
)

export function isSettingsSectionKey(
	section: string,
): section is SettingsSectionKey {
	return settingsSectionKeys.has(section as SettingsSectionKey)
}

export function getSettingsSectionKeyFromSlug(
	slug: string,
): SettingsSectionKey | undefined {
	const key = settingsSectionSlugToKey[slug]
	return key && isSettingsSectionKey(key) ? key : undefined
}

export function getSettingsEntitySectionKey(sectionKey: SettingsSectionKey) {
	return settingsSectionMeta[sectionKey].EntitySection
}

export function getSettingsSectionSubmitPayload(
	sectionKey: SettingsSectionKey,
	values: Record<string, unknown>,
) {
	const sectionValues = values[sectionKey]
	if (sectionValues && typeof sectionValues === 'object') {
		return sectionValues
	}

	const hasSectionScopedKeys = Object.keys(values).some((key) =>
		settingsSectionKeys.has(key as SettingsSectionKey),
	)
	return hasSectionScopedKeys ? {} : values
}

export const tenantSettingsTabs = Object.entries(tenantSettingsSectionMeta).map(
	([key, value]) => ({
		key: key as TenantSettingsSectionKey,
		label: value.label,
		slug: getSettingsSectionSlug(value),
	}),
) satisfies Array<SettingsTab<TenantSettingsSectionKey>>

export const tenantSettingsLabelByKey: Record<
	TenantSettingsSectionKey,
	string
> = Object.fromEntries(
	tenantSettingsTabs.map((tab) => [tab.key, tab.label]),
) as unknown as Record<TenantSettingsSectionKey, string>

export const tenantSettingsSlugByKey: Record<TenantSettingsSectionKey, string> =
	Object.fromEntries(
		tenantSettingsTabs.map((tab) => [tab.key, tab.slug]),
	) as Record<TenantSettingsSectionKey, string>

export const tenantSettingsKeyBySlug = Object.fromEntries(
	tenantSettingsTabs.map((tab) => [tab.slug, tab.key]),
) as Record<string, TenantSettingsSectionKey>

const tenantSettingsKeys = new Set<TenantSettingsSectionKey>(
	tenantSettingsTabs.map((tab) => tab.key),
)

export const tenantSettingsSectionEndpoints: Record<
	TenantSettingsSectionKey,
	string
> = Object.fromEntries(
	Object.entries(tenantSettingsSectionMeta).map(([key, value]) => [
		key,
		getGeneratedSettingsSectionPath(value.EntitySection) ??
			`settings/${getGeneratedSettingsSection(value.EntitySection).key}`,
	]),
) as Record<TenantSettingsSectionKey, string>

export function isTenantSettingsSectionKey(
	section: string,
): section is TenantSettingsSectionKey {
	return tenantSettingsKeys.has(section as TenantSettingsSectionKey)
}

export function getTenantSettingsSectionKeyFromSlug(
	slug: string,
): TenantSettingsSectionKey | undefined {
	const key = tenantSettingsKeyBySlug[slug]
	return key && isTenantSettingsSectionKey(key) ? key : undefined
}

export const settingsFormSections: FormSection[] = [
	{
		fields: [
			{
				name: 'contactSettings.canDenyContacts',
				label: messages.entity_canDenyContacts(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'contactSettings.canRedirectContacts',
				label: messages.entity_canRedirectContacts(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'contactSettings.canPauseWrapUpTime',
				label: messages.entity_canPauseWrapUpTime(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'contactSettings.maxSimultaneousContacts',
				label: messages.entity_maxSimultaneousContacts(),
				type: 'number',
				initialValue: 0,
			},
			{
				name: 'contactSettings.wrapUpTimePercent',
				label: messages.entity_wrapUpTimePercent(),
				type: 'number',
				initialValue: 0,
			},
			{
				name: 'contactSettings.showWrapUpTime',
				label: messages.entity_showWrapUpTime(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'contactSettings.canExtendWrapupTime',
				label: messages.entity_canExtendWrapupTime(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'contactSettings.extendWrapupCount',
				label: messages.entity_extendWrapupCount(),
				type: 'number',
				initialValue: 0,
			},
			{
				name: 'contactSettings.extendWrapupSeconds',
				label: messages.entity_extendWrapupSeconds(),
				type: 'number',
				initialValue: 0,
			},
			{
				name: 'contactSettings.canSelectContactCodes',
				label: messages.entity_canSelectContactCodes(),
				type: 'checkbox',
				initialValue: false,
			},
		],
	},
	{
		fields: [
			{
				name: 'loginSettings.disableLoginScreen',
				label: messages.entity_disableLoginScreen(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'loginSettings.extension',
				label: messages.entity_extension(),
				type: 'text',
				initialValue: '',
			},
			{
				name: 'loginSettings.defaultPauseReasonId',
				label: messages.entity_defaultPauseReasonId(),
				type: 'select',
				initialValue: null,
				options: [],
			},
			{
				name: 'loginSettings.isUserAllowedToChangePauseReasonInLoginMask',
				label: messages.entity_isUserAllowedToChangePauseReasonInLoginMask(),
				type: 'checkbox',
				initialValue: false,
			},
		],
	},
	{
		fields: [
			{
				name: 'phoneSettings.softphoneUrl',
				label: messages.entity_softphoneUrl(),
				type: 'url',
				initialValue: '',
			},
			{
				name: 'phoneSettings.isSoftphoneEnabled',
				label: messages.entity_isSoftphoneEnabled(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'phoneSettings.isCallHistoryEnabled',
				label: messages.entity_isCallHistoryEnabled(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'phoneSettings.isPhoneBookEnabled',
				label: messages.entity_isPhoneBookEnabled(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'phoneSettings.isCameraEnabledByDefault',
				label: messages.entity_isCameraEnabledByDefault(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'phoneSettings.isUserAllowedToToggleCamera',
				label: messages.entity_isUserAllowedToToggleCamera(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'phoneSettings.isUserAllowedToAccessCameraSettings',
				label: messages.entity_isUserAllowedToAccessCameraSettings(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'phoneSettings.isSpeakerEnabledByDefault',
				label: messages.entity_isSpeakerEnabledByDefault(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'phoneSettings.isUserAllowedToAccessSpeakerSettings',
				label: messages.entity_isUserAllowedToAccessSpeakerSettings(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'phoneSettings.isUserAllowedToToggleSpeaker',
				label: messages.entity_isUserAllowedToToggleSpeaker(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'phoneSettings.isMicEnabledByDefault',
				label: messages.entity_isMicEnabledByDefault(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'phoneSettings.isUserAllowedToAccessMicSettings',
				label: messages.entity_isUserAllowedToAccessMicSettings(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'phoneSettings.isUserAllowedToToggleMic',
				label: messages.entity_isUserAllowedToToggleMic(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'phoneSettings.isScreenSharingAllowed',
				label: messages.entity_isScreenSharingAllowed(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'phoneSettings.isBackgroundBlurringEnabledByDefault',
				label: messages.entity_isBackgroundBlurringEnabledByDefault(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'phoneSettings.isBackgroundBlurringAllowed',
				label: messages.entity_isBackgroundBlurringAllowed(),
				type: 'checkbox',
				initialValue: false,
			},
		],
	},
	{
		fields: [
			{
				name: 'generalSettings.showRonaPopup',
				label: messages.entity_showRonaPopup(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'generalSettings.isCTIToolbarEnabled',
				label: messages.entity_isCTIToolbarEnabled(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'generalSettings.showNotificationBell',
				label: messages.entity_showNotificationBell(),
				type: 'checkbox',
				initialValue: false,
			},
		],
	},
	{
		fields: [
			{
				name: 'setClipSettings.isActive',
				label: messages.entity_isActive(),
				type: 'checkbox',
				initialValue: false,
			},
		],
	},
	{
		fields: [
			{
				name: 'recordingSettings.showIndicator',
				label: messages.entity_showIndicator(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'recordingSettings.showStartButton',
				label: messages.entity_showStartButton(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'recordingSettings.showStopButton',
				label: messages.entity_showStopButton(),
				type: 'checkbox',
				initialValue: false,
			},
			{
				name: 'recordingSettings.showPauseButton',
				label: messages.entity_showPauseButton(),
				type: 'checkbox',
				initialValue: false,
			},
		],
	},
]

export function getSettingsSectionConfig(
	sectionKey: SettingsSectionKey,
): FormSection {
	const section = settingsFormSections.find((candidate) =>
		candidate.fields.some((field) => field.name.startsWith(`${sectionKey}.`)),
	)

	return section ?? { fields: [] }
}

export function getTenantSettingsSectionConfig(
	sectionKey: TenantSettingsSectionKey,
): FormSection {
	return getSettingsSectionConfig(sectionKey)
}

export function buildSettingsInitialValues(
	settings?: TemplateSettingsDto,
): SettingsFormValues {
	const rawDefaultPauseReasonId = settings?.loginSettings?.defaultPauseReasonId
	const defaultPauseReasonId =
		typeof rawDefaultPauseReasonId === 'string' ||
		typeof rawDefaultPauseReasonId === 'number'
			? String(rawDefaultPauseReasonId)
			: null

	return {
		contactSettings: {
			canDenyContacts: settings?.contactSettings?.canDenyContacts ?? false,
			canRedirectContacts:
				settings?.contactSettings?.canRedirectContacts ?? false,
			canPauseWrapUpTime:
				settings?.contactSettings?.canPauseWrapUpTime ?? false,
			maxSimultaneousContacts:
				settings?.contactSettings?.maxSimultaneousContacts ?? 0,
			wrapUpTimePercent: settings?.contactSettings?.wrapUpTimePercent ?? 0,
			showWrapUpTime: settings?.contactSettings?.showWrapUpTime ?? false,
			canExtendWrapupTime:
				settings?.contactSettings?.canExtendWrapupTime ?? false,
			extendWrapupCount: settings?.contactSettings?.extendWrapupCount ?? 0,
			extendWrapupSeconds: settings?.contactSettings?.extendWrapupSeconds ?? 0,
			canSelectContactCodes:
				settings?.contactSettings?.canSelectContactCodes ?? false,
		},
		loginSettings: {
			disableLoginScreen: settings?.loginSettings?.disableLoginScreen ?? false,
			extension: settings?.loginSettings?.extension ?? '',
			defaultPauseReasonId,
			isUserAllowedToChangePauseReasonInLoginMask:
				settings?.loginSettings?.isUserAllowedToChangePauseReasonInLoginMask ??
				false,
		},
		phoneSettings: {
			softphoneUrl: settings?.phoneSettings?.softphoneUrl ?? '',
			isSoftphoneEnabled: settings?.phoneSettings?.isSoftphoneEnabled ?? false,
			isCallHistoryEnabled:
				settings?.phoneSettings?.isCallHistoryEnabled ?? false,
			isPhoneBookEnabled: settings?.phoneSettings?.isPhoneBookEnabled ?? false,
			isCameraEnabledByDefault:
				settings?.phoneSettings?.isCameraEnabledByDefault ?? false,
			isUserAllowedToToggleCamera:
				settings?.phoneSettings?.isUserAllowedToToggleCamera ?? false,
			isUserAllowedToAccessCameraSettings:
				settings?.phoneSettings?.isUserAllowedToAccessCameraSettings ?? false,
			isSpeakerEnabledByDefault:
				settings?.phoneSettings?.isSpeakerEnabledByDefault ?? false,
			isUserAllowedToAccessSpeakerSettings:
				settings?.phoneSettings?.isUserAllowedToAccessSpeakerSettings ?? false,
			isUserAllowedToToggleSpeaker:
				settings?.phoneSettings?.isUserAllowedToToggleSpeaker ?? false,
			isMicEnabledByDefault:
				settings?.phoneSettings?.isMicEnabledByDefault ?? false,
			isUserAllowedToAccessMicSettings:
				settings?.phoneSettings?.isUserAllowedToAccessMicSettings ?? false,
			isUserAllowedToToggleMic:
				settings?.phoneSettings?.isUserAllowedToToggleMic ?? false,
			isScreenSharingAllowed:
				settings?.phoneSettings?.isScreenSharingAllowed ?? false,
			isBackgroundBlurringEnabledByDefault:
				settings?.phoneSettings?.isBackgroundBlurringEnabledByDefault ?? false,
			isBackgroundBlurringAllowed:
				settings?.phoneSettings?.isBackgroundBlurringAllowed ?? false,
		},
		generalSettings: {
			showRonaPopup: settings?.generalSettings?.showRonaPopup ?? false,
			isCTIToolbarEnabled:
				settings?.generalSettings?.isCTIToolbarEnabled ?? false,
			showNotificationBell:
				settings?.generalSettings?.showNotificationBell ?? false,
		},
		setClipSettings: {
			isActive: settings?.setClipSettings?.isActive ?? false,
		},
		recordingSettings: {
			showIndicator: settings?.recordingSettings?.showIndicator ?? false,
			showStartButton: settings?.recordingSettings?.showStartButton ?? false,
			showStopButton: settings?.recordingSettings?.showStopButton ?? false,
			showPauseButton: settings?.recordingSettings?.showPauseButton ?? false,
		},
	}
}

export function buildTenantSettingsInitialValues(
	settings?: Partial<TenantSettingsDto>,
): SettingsFormValues {
	return {
		...buildSettingsInitialValues(settings),
	}
}
